import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { DialogModule, DialogService } from '@progress/kendo-angular-dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InsightsService } from '@maximizer/core/shared/insights';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import { SyncConfigurationComponent } from '../configuration/sync-configuration.component';
import {
  OAuth2Service,
  PopupSessionExpiredService,
} from '@maximizer/oauth2/data-access';
import { TrackerService } from '@maximizer/outlook/shared/data-access';
import { OutlookSyncStore } from '@maximizer/outlook-sync/data-access';
import {
  InstallEntrySource,
  InstallResumeResult,
  InstallStartResult,
  InstallStep,
  SyncConfigurationInstallOrchestrator,
} from '../install';

const TRACKER_MODULE = 'sync-configuration';

const CURATED_ERROR_BODY_KEYS: Partial<Record<InstallStep, string>> = {
  'session-validation': 'outlookSync.install.sessionErrorDialogBody',
  'session-unavailable': 'outlookSync.install.sessionErrorDialogBody',
  'profile-email-missing': 'outlookSync.install.emailErrorDialogBody',
  'session-identity-mismatch': 'outlookSync.install.identityErrorDialogBody',
  'microsoft-oauth-denied-or-cancelled':
    'outlookSync.install.cancelledDialogBody',
};

type DialogKind = 'session-expired' | 'install-failed' | 'reauth-success';

@Component({
  selector: 'maximizer-sync-configuration-shell',
  standalone: true,
  imports: [
    TranslateModule,
    DialogModule,
    SyncConfigurationComponent,
    ...UI_LOADER,
  ],
  templateUrl: './shell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SyncConfigurationShellComponent implements AfterViewInit {
  protected configuration =
    viewChild.required<SyncConfigurationComponent>('configuration');

  protected readonly orchestrator = inject(SyncConfigurationInstallOrchestrator);
  private readonly route = inject(ActivatedRoute);
  private readonly dialogService = inject(DialogService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly insights = inject(InsightsService);
  private readonly oAuthService = inject(OAuth2Service);
  private readonly sessionExpired = inject(PopupSessionExpiredService).expired;
  private readonly tracker = inject(TrackerService);
  private readonly store = inject(OutlookSyncStore);

  protected readonly shownDialog = computed<DialogKind | null>(() => {
    if (this.sessionExpired() !== null) return 'session-expired';
    if (this.orchestrator.failure() !== null) return 'install-failed';
    if (this.store.reauthSucceeded()) return 'reauth-success';
    return null;
  });

  protected readonly closeBlocked = signal(false);

  protected readonly installLoaderVisible = computed(() => {
    const attempt = this.orchestrator.attempt();
    if (!attempt) return false;
    if (attempt.failure) return false;
    if (this.sessionExpired() !== null) return false;
    return attempt.step !== 'success' && attempt.step !== 'install-superseded';
  });

  private dialogShown = false;

  constructor() {
    effect(() => {
      const which = this.shownDialog();
      if (!which || this.dialogShown) return;
      this.dialogShown = true;
      if (which === 'session-expired') {
        this.emitSessionExpiredTelemetry();
        this.openFailureDialog(
          'outlookSync.sessionExpired.body',
          'outlookSync.sessionExpired.title',
          'session-expired',
        );
      } else if (which === 'reauth-success') {
        this.openFailureDialog(
          'outlookSync.reauthSuccess.body',
          'outlookSync.reauthSuccess.title',
          'reauth-success',
          {
            actionTextKey: 'outlookSync.dialogs.continue',
            closeWindowOnDismiss: false,
          },
        );
      } else {
        this.openInstallFailureDialog();
      }
    });
  }

  private openInstallFailureDialog(): void {
    const failure = this.orchestrator.failure();
    const curatedBodyKey = failure?.step
      ? CURATED_ERROR_BODY_KEYS[failure.step]
      : undefined;
    this.openFailureDialog(
      curatedBodyKey ??
        failure?.dialogBodyKey ??
        'outlookSync.install.errorDialogBody',
      'outlookSync.install.errorDialogTitle',
      'install-failed',
    );
  }

  ngAfterViewInit(): void {
    void this.dispatch();
  }

  private async dispatch(): Promise<void> {
    try {
      const result = await this.runInstallFlow();
      if (result === null) return;
      this.handleFinalState(result);
    } catch (error) {
      this.handleDispatchError(error);
    }
  }

  private async runInstallFlow(): Promise<
    InstallStartResult | InstallResumeResult | null
  > {
    if (this.hasInstallCallbackParams()) {
      return this.runCallbackResume();
    }
    return this.runFreshStart();
  }

  private async runCallbackResume(): Promise<InstallResumeResult | null> {
    const result = await this.orchestrator.resume({
      navigateOnSuccess: false,
      entrySource: 'callback',
    });
    history.replaceState({}, '', '/sync-configuration');
    if (result.type === 'success' || result.type === 'superseded') {
      this.configuration().doAction('show', null);
      return null;
    }
    return result;
  }

  private async runFreshStart(): Promise<InstallStartResult | null> {
    const forceInstall = this.installParamPresent();
    const entrySource: InstallEntrySource = forceInstall ? 'cta' : 'bookmark';
    const result = await this.orchestrator.start({ entrySource, forceInstall });
    if (result.type === 'existing') {
      this.configuration().doAction('show', result.record);
      return null;
    }
    if (result.type === 'redirected') {
      return null;
    }
    return result;
  }

  private handleFinalState(
    result: InstallStartResult | InstallResumeResult,
  ): void {
    if (this.dialogShown) return;
    this.dialogShown = true;
    if (result.type === 'session-expired') {
      this.openFailureDialog(
        'outlookSync.sessionExpired.body',
        'outlookSync.sessionExpired.title',
        'session-expired',
      );
      return;
    }
    this.openInstallFailureDialog();
  }

  private handleDispatchError(error: unknown): void {
    console.error('[sync-configuration-shell] dispatch failed', error);
    if (this.dialogShown) return;
    this.dialogShown = true;
    if (this.sessionExpired() !== null) {
      this.openFailureDialog(
        'outlookSync.sessionExpired.body',
        'outlookSync.sessionExpired.title',
        'session-expired',
      );
      return;
    }
    this.openInstallFailureDialog();
  }

  private hasInstallCallbackParams(): boolean {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('state')?.startsWith('install-callback:')) return false;
    return ['code', 'error', 'error_description'].some((param) =>
      params.has(param),
    );
  }

  private installParamPresent(): boolean {
    return this.route.snapshot.queryParamMap.get('install') === 'true';
  }

  private emitSessionExpiredTelemetry(): void {
    const source = this.sessionExpired() ?? 'unknown';
    const maximizerUserUid = this.oAuthService.getStorageMxUserId() ?? '';
    this.insights.trackEvent({
      name: 'sync-configuration.session-expired',
      properties: {
        feature: 'sync-configuration',
        source,
        maximizerUserUid,
        timestampUtc: new Date().toISOString(),
      },
    });
    this.trackSyncConfigurationEvent('session-expired', 'executed', {
      source,
      ...(maximizerUserUid ? { maximizerUserUid } : {}),
    });
  }

  private openFailureDialog(
    bodyKey: string,
    titleKey?: string,
    dialogKind?: DialogKind,
    opts?: { actionTextKey?: string; closeWindowOnDismiss?: boolean },
  ): void {
    const actionText = this.translate.instant(
      opts?.actionTextKey ?? 'outlookSync.dialogs.close',
    );
    const closeWindowOnDismiss = opts?.closeWindowOnDismiss ?? true;
    const title = titleKey ? this.translate.instant(titleKey) : undefined;

    const dialog = this.dialogService.open({
      title,
      content: this.translate.instant(bodyKey),
      actions: [
        { text: actionText, themeColor: 'primary', cssClass: 'mt-12' },
      ],
      actionsLayout: 'end',
      width: 440,
      minWidth: 280,
      cssClass: '[&_.k-dialog-content]:text-justify',
    });

    if (dialogKind) {
      this.trackSyncConfigurationEvent('dialog', 'opened', {
        dialog: dialogKind,
      });
    }

    dialog.result
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (dialogKind) {
          this.trackSyncConfigurationEvent('dialog', 'closed', {
            dialog: dialogKind,
          });
        }
        if (!closeWindowOnDismiss) return;
        if (this.attemptCloseWindow()) return;
        // Browser blocked the close — window not script-opened. Show fallback.
        this.closeBlocked.set(true);
      });
  }

  private trackSyncConfigurationEvent(
    journey: string,
    action: string,
    metadata: Record<string, unknown>,
  ): void {
    this.tracker.trackEvent({
      module: TRACKER_MODULE,
      journey,
      action,
      metadata,
    });
  }

  private attemptCloseWindow(): boolean {
    try {
      window.close();
    } catch {
      // best-effort
    }
    return window.closed;
  }
}
